import { withCurrencyConfiguration } from "config/currencyServer";
import React, { FC, useEffect, useState } from "react";
import { Expert as IExpert, EventItem } from "types/user";
import ClientNav from "components/ClientNav/ClientNav";
import EventPage from "screens/server/EventPage";
import "i18n";
import { BrowserRouter } from "react-router-dom";
import dynamic from "next/dynamic";
import { ErrorBoundary } from "@sentry/nextjs";
import ErrorView from "components/ErrorView/ErrorView";
import AuthModals from "components/AuthModals/AuthModals";
import {
  AssignedSeatingMap,
  loadPublicAssignedSeatingMap,
} from "screens/browser/client/offer/Event/models/assignedSeating";

const AppEntryPoint = dynamic(() => import("screens/browser/App"), {
  ssr: false,
});

export const Event: FC<{
  expert: IExpert;
  event: EventItem;
  acceptLanguageHeader: string;
  query;
  isAuthenticated: boolean;
  assignedSeatingMap?: AssignedSeatingMap | null;
}> = ({
  expert,
  event,
  acceptLanguageHeader,
  query,
  isAuthenticated,
  assignedSeatingMap,
}) => {
  const [isClient, setIsClient] = useState(false);
  const [resolvedAssignedSeatingMap, setResolvedAssignedSeatingMap] =
    useState(assignedSeatingMap);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (isAuthenticated || !event) {
      return;
    }

    if (assignedSeatingMap) {
      setResolvedAssignedSeatingMap(assignedSeatingMap);
      return;
    }

    loadPublicAssignedSeatingMap({
      event,
      eventId: event.offerSlug,
      restUrl: "/api",
    }).then((seatMap) => {
      if (seatMap) {
        setResolvedAssignedSeatingMap(seatMap);
      }
    });
  }, [assignedSeatingMap, event, isAuthenticated]);

  if (isAuthenticated) {
    return isClient ? (
      <ErrorBoundary fallback={() => <ErrorView />}>
        <BrowserRouter>
          <AuthModals />
          <AppEntryPoint />
        </BrowserRouter>
      </ErrorBoundary>
    ) : null;
  }
  return (
    <>
      <ClientNav
        hasButtons={false}
        headerColor={true}
        acceptLanguageHeader={acceptLanguageHeader}
      />
      <div className="md:pt-20">
        <EventPage
          expert={expert}
          event={event}
          query={query}
          assignedSeatingMap={resolvedAssignedSeatingMap}
        />
      </div>
    </>
  );
};

const getPageServerSideProps = async ({ req, query, resolvedUrl }) => {
  const cookies = req.cookies;

  const user = await fetch(
    process.env.NEXT_PUBLIC_REST_URL + "/users/v1/users/me",
    {
      headers: {
        cookie: `access_token=${cookies?.access_token}; refresh_token=${cookies?.refresh_token}`,
      },
    }
  ).then((res) => res.json());

  const isAuthenticated = Boolean(user?.id);

  // Select the browser path before any public redirects or data requests.
  if (isAuthenticated) {
    return { props: { isAuthenticated, resolvedUrl } };
  }

  const acceptLanguageHeader = Array.isArray(req.headers["accept-language"])
    ? req.headers["accept-language"][0] || ""
    : req.headers["accept-language"] || "";

  if (resolvedUrl.startsWith("/my/event/")) {
    return {
      redirect: {
        destination: "/sign-in?returnTo=" + resolvedUrl,
        permanent: false,
      },
    };
  }

  const publicId = query?.publicId;
  const eventId = query?.eventId;

  const expert = await fetch(
    process.env.NEXT_PUBLIC_REST_URL + "/experts/v1/experts/" + publicId
  ).then((res) => res.json());

  const newPublicId = expert.publicId;

  if (publicId !== newPublicId) {
    return {
      redirect: {
        destination: `/${newPublicId}/event/${eventId}`,
        permanent: false,
      },
    };
  }

  const event = await fetch(
    `${process.env.NEXT_PUBLIC_REST_URL}/offers/v1/event-offer/${eventId}`
  )
    .then((res) => {
      if (!res.ok) {
        return null;
      }
      return res.json();
    })
    .catch(() => {
      return null;
    });

  const assignedSeatingMap = await loadPublicAssignedSeatingMap({
    event,
    eventId,
    restUrl: process.env.NEXT_PUBLIC_REST_URL,
  });

  if (expert?.id) {
    return {
      props: {
        expert,
        event,
        assignedSeatingMap,
        acceptLanguageHeader,
        query,
        isAuthenticated,
      },
    };
  } else {
    return {
      notFound: true,
    };
  }
};

export const getServerSideProps = withCurrencyConfiguration(
  getPageServerSideProps
);

export default Event;
